import axiosIns from '@/libs/axios';

const canFetchNotifications = (rootGetters) => {
    return rootGetters['auth/hasAnyPermission']([
        'pas.payment.accounts.view',
        'pas.transaction.withdraws.view',
        'pas.transaction.deposits.view',
        'pas.notification.tenant-notifications.view'
    ]);
};

export const state = {
    // Alert notifications
    type: null,
    message: null,
    // Transaction counts
    processDepositCount: 0,
    pendingWithdrawalCount: 0,
    criticalAccountCount: 0,
    // Initialization flag
    isInitialized: false,
    initPromise: null
};

export const mutations = {
    // Alert mutations
    success(state, message) {
        state.type = 'alert-success';
        state.message = message;
    },
    error(state, message) {
        state.type = 'alert-danger';
        state.message = message;
    },
    clear(state) {
        state.type = null;
        state.message = null;
    },
    // Count mutations
    SET_PROCESS_DEPOSIT_COUNT(state, count) {
        state.processDepositCount = count;
    },
    SET_PENDING_WITHDRAWAL_COUNT(state, count) {
        state.pendingWithdrawalCount = count;
    },
    SET_CRITICAL_ACCOUNT_COUNT(state, count) {
        state.criticalAccountCount = count;
    },
    UPDATE_COUNTS(state, { processDepositCount, pendingWithdrawalCount, criticalAccountCount }) {
        state.processDepositCount = processDepositCount;
        state.pendingWithdrawalCount = pendingWithdrawalCount;
        state.criticalAccountCount = criticalAccountCount;
    },
    SET_INITIALIZED(state, value) {
        state.isInitialized = value;
    },
    SET_INIT_PROMISE(state, promise) {
        state.initPromise = promise;
    }
};

export const getters = {
    // Alert getters
    type: (state) => state.type,
    message: (state) => state.message,
    // Count getters
    processDepositCount: (state) => state.processDepositCount,
    pendingWithdrawalCount: (state) => state.pendingWithdrawalCount,
    criticalAccountCount: (state) => state.criticalAccountCount,
    isInitialized: (state) => state.isInitialized
};

export const actions = {
    // Alert actions
    success({ commit }, message) {
        commit('success', message);
    },
    error({ commit }, message) {
        commit('error', message);
    },
    clear({ commit }) {
        commit('clear');
    },
    // Count actions
    updateCounts({ commit }, { process_deposit_count, pending_withdrawal_count, criticalAccountCount }) {
        commit('UPDATE_COUNTS', {
            processDepositCount: process_deposit_count,
            pendingWithdrawalCount: pending_withdrawal_count,
            criticalAccountCount: criticalAccountCount
        });
    },
    setProcessDepositCount({ commit }, count) {
        commit('SET_PROCESS_DEPOSIT_COUNT', count);
    },
    setPendingWithdrawalCount({ commit }, count) {
        commit('SET_PENDING_WITHDRAWAL_COUNT', count);
    },
    setCriticalAccountCount({ commit }, count) {
        commit('SET_CRITICAL_ACCOUNT_COUNT', count);
    },
    // Initialize notification counts
    async init({ commit, state, rootGetters }) {

        // Check if already initialized
        if (state.isInitialized) {
            return;
        }

        if (state.initPromise) {
            return state.initPromise;
        }

        // Create a new init promise
        const promise = (async () => {
            // Set counts to 0 by default first
            commit('UPDATE_COUNTS', {
                processDepositCount: 0,
                pendingWithdrawalCount: 0,
                criticalAccountCount: 0
            });

            // Check permissions before making API call
            if (!canFetchNotifications(rootGetters)) {
                commit('SET_INITIALIZED', true);
                commit('SET_INIT_PROMISE', null);
                return;
            }

            try {
                const response = await axiosIns.get('/service/payment/api/tenant/v1/notification/details');
                const data = response.data.data;

                // Update counts with API response
                commit('UPDATE_COUNTS', {
                    processDepositCount: data.processDepositCount || 0,
                    pendingWithdrawalCount: data.pendingWithdrawalCount || 0,
                    criticalAccountCount: data.criticalAccountCount || 0
                });

                // Mark as initialized
                commit('SET_INITIALIZED', true);
            } catch (error) {
                console.error('Failed to fetch notification details:', error);
                // Explicitly set counts to 0 if API fails
                commit('UPDATE_COUNTS', {
                    processDepositCount: 0,
                    pendingWithdrawalCount: 0,
                    criticalAccountCount: 0
                });
                // Still mark as initialized to prevent repeated failed attempts
                commit('SET_INITIALIZED', true);
            } finally {
                commit('SET_INIT_PROMISE', null);
            }
        })();

        commit('SET_INIT_PROMISE', promise);

        return promise;
    },
    // Force refresh notification counts (bypasses initialization check)
    async forceRefresh({ commit, rootGetters }) {
        // Check permissions before making API call
        if (!canFetchNotifications(rootGetters)) return;

        // Set counts to 0 by default first
        commit('UPDATE_COUNTS', {
            processDepositCount: 0,
            pendingWithdrawalCount: 0,
            criticalAccountCount: 0
        });

        try {
            // Call API to get notification details
            const response = await axiosIns.get('/service/payment/api/tenant/v1/notification/details');
            const data = response.data.data;

            // Update counts with API response
            commit('UPDATE_COUNTS', {
                processDepositCount: data.processDepositCount || 0,
                pendingWithdrawalCount: data.pendingWithdrawalCount || 0,
                criticalAccountCount: data.criticalAccountCount || 0
            });
        } catch (error) {
            console.error('Failed to fetch notification details:', error);
            // Explicitly set counts to 0 if API fails
            commit('UPDATE_COUNTS', {
                processDepositCount: 0,
                pendingWithdrawalCount: 0,
                criticalAccountCount: 0
            });
        }
    },

    // Reset initialization state (useful for logout)
    reset({ commit }) {
        commit('SET_INITIALIZED', false);
        commit('UPDATE_COUNTS', {
            processDepositCount: 0,
            pendingWithdrawalCount: 0,
            criticalAccountCount: 0
        });
    },

    async notificationDetails({ rootGetters }) {
        if (!canFetchNotifications(rootGetters)) {
            return [];
        }

        try {
            const response = await axiosIns.get('/service/payment/api/tenant/v1/notification/portal-notifications');
            return response.data.data.data || [];

        } catch (error) {
            console.error('Failed to fetch notification details:', error);
            return [];
        }
    },
};
